import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { TbChevronLeft, TbCash, TbShoppingCart } from 'react-icons/tb';
import { FaArrowLeft, FaCalendar, FaMoneyBill, FaTrash, FaUsers } from 'react-icons/fa';
import PageHeader from '../../components/misc/PageHeader';
import { BalanceStats, BankTransactionWithParticipation } from '../../../types/bankAccount';
import * as bankAccountService from '../../../services/bankAccountService';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';
import { formatDate } from '../../../utils/stringUtils';
import { formatEuroCents, formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
import { RootState } from '../../../redux/reducers';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import Button from '../../components/buttons/Button';
import PageWithTabsLayout from '../../components/layouts/PageWithTabsLayout';
import TransactionProcessor from '../../components/financial/TransactionProcessor';
import AreYouSureModal from '../../components/modals/AreYouSureModal';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import { C2BOrderDto, PaymentOrderStatus } from '../../../services/model/marketplace.model';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import { deleteOrder, getOrdersForUser } from '../../../services/orders.marketplace.service';

type TabType = 'personal' | 'contribution' | 'sales';

export default function FinancialMemberInformationPage() {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.memberInformation' });
  const navigate = useNavigate();
  const params = useParams();
  const connections = useSelector((state: RootState) => state.connections).filter(
    (c) => c.type === ConnectionType.B2C_CONNECTION,
  );

  const [activeTab, setActiveTab] = useState<TabType>('personal');
  const [transactions, setTransactions] = useState<BankTransactionWithParticipation[] | null>();
  const [contributionBalances, setContributionBalances] = useState<Record<
    number,
    BalanceStats
  > | null>();
  const [loading, setLoading] = useState(true);

  const connectionId = params.connectionId ? +params.connectionId : null;
  const connection = connections.find((c) => +c.id === connectionId!);

  useEffect(() => {
    if (!connectionId) {
      navigate('/financial/reports');
      return;
    }

    setLoading(true);
    Promise.all([
      bankAccountService.getAllTransactions(),
      bankAccountService.getContributionBalancesForConnections(),
    ])
      .then(([tns, bs]) => {
        setTransactions(tns);
        setContributionBalances(bs);
        setLoading(false);
      })
      .catch(() => {
        setTransactions(null);
        setLoading(false);
      });
  }, [connectionId, navigate]);

  // Calculate member-specific data
  const memberData = useMemo(() => {
    if (!transactions || !contributionBalances || !connectionId) return null;

    // Personal transactions
    const personalTransactions = transactions.filter((transaction) =>
      transaction.participations?.some((p) => p.connectionId === connectionId),
    );

    const personalStats = personalTransactions.reduce(
      (stats, transaction) => {
        const participation = transaction.participations?.find(
          (p) => p.connectionId === connectionId,
        );
        if (participation) {
          if (participation.amount > 0) {
            stats.income += participation.amount;
          } else {
            stats.expenses += Math.abs(participation.amount);
          }
          stats.balance += participation.amount;
        }
        return stats;
      },
      { income: 0, expenses: 0, balance: 0 },
    );

    // Contribution transactions
    const contributionStats = contributionBalances[connectionId] || {
      income: 0,
      expenses: 0,
      balance: 0,
    };

    // Sales transactions (placeholder for future implementation)
    const salesStats = { income: 0, expenses: 0, balance: 0 };

    return {
      personal: {
        stats: personalStats,
        transactions: personalTransactions,
      },
      contribution: {
        stats: contributionStats,
        transactions: transactions.filter(
          (transaction) => transaction.contributionPayment?.connectionId === connectionId,
        ),
      },
      sales: {
        stats: salesStats,
        transactions: [], // Placeholder for sales transactions
      },
    };
  }, [transactions, contributionBalances, connectionId]);

  if (!connectionId || !connection) {
    toast.error(t('error.memberNotFound'));
    navigate('/financial/reports');
    return null;
  }

  if (loading) {
    return <CgSpinner size={150} className="mx-auto mt-20 animate-spin text-primary-300" />;
  }

  if (!memberData) {
    toast.error(t('error.dataNotFound'));
    navigate('/financial/reports');
    return null;
  }

  const tabs: TabType[] = ['personal', 'contribution', 'sales'];

  const renderTabContent = (tab: TabType) => {
    switch (tab) {
      case 'personal':
        return <PersonalTab data={memberData.personal} connection={connection} />;
      case 'contribution':
        return <ContributionTab data={memberData.contribution} />;
      case 'sales':
        return <SalesTab connectionId={connectionId} />;
      default:
        return <PersonalTab data={memberData.personal} connection={connection} />;
    }
  };

  return (
    <>
      <Link to="/financial/member-balance">
        <Button className="mb-2 gap-1 p-0">
          <TbChevronLeft size={20} /> {t('back')}
        </Button>
      </Link>

      <PageHeader
        title={`${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`}
        subtitle={t('subtitle')}
      />

      <PageWithTabsLayout
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        renderTabContent={renderTabContent}
      />
    </>
  );
}

// Personal Tab Component
function PersonalTab({ data, connection }: { data: any; connection: any }) {
  const { t: tPersonal, i18n } = useTranslation('', {
    keyPrefix: 'page.financial.memberInformation.personal',
  });

  return (
    <div className="w-full">
      {/* Stats Tiles */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TbCash className="h-8 w-8 text-success" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-500">{tPersonal('tiles.balance')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatEuroCentsWithSign(data.stats.balance, i18n.language)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TbCash className="h-8 w-8 text-primary-300" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-500">{tPersonal('tiles.income')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatEuroCents(data.stats.income, i18n.language)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TbCash className="h-8 w-8 text-danger" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-500">{tPersonal('tiles.expenses')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatEuroCents(data.stats.expenses, i18n.language)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="relative w-full overflow-x-auto">
        <BaseTable>
          <BaseTableHeader>
            <BaseTableHeaderRow>
              <HeaderCell>{tPersonal('table.date')}</HeaderCell>
              <HeaderCell>{tPersonal('table.description')}</HeaderCell>
              <HeaderCell>{tPersonal('table.amount')}</HeaderCell>
              <HeaderCell>{tPersonal('table.type')}</HeaderCell>
              <HeaderCell>{tPersonal('table.processedBy')}</HeaderCell>
            </BaseTableHeaderRow>
          </BaseTableHeader>
          <BaseTableBody>
            <BaseTableSpacerRow />
            {data.transactions.length === 0 ? (
              <BaseTableDataRow>
                <td colSpan={5} className="py-8 text-center text-gray-500">
                  {tPersonal('noTransactions')}
                </td>
              </BaseTableDataRow>
            ) : (
              data.transactions.map((transaction: any) => {
                const participation = transaction.participations?.find(
                  (p: any) => p.connectionId === connection.id,
                );
                if (!participation) return null;

                return (
                  <BaseTableDataRow key={transaction.transactionId}>
                    <DataCell>{formatDate(transaction.date)}</DataCell>
                    <DataCell>{transaction.description}</DataCell>
                    <DataCell>
                      {formatEuroCentsWithSign(participation.amount, i18n.language)}
                    </DataCell>
                    <DataCell>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          participation.amount > 0
                            ? 'bg-green-100 text-success'
                            : 'bg-red-100 text-danger'
                        }`}>
                        {participation.amount > 0
                          ? tPersonal('table.income')
                          : tPersonal('table.expense')}
                      </span>
                    </DataCell>
                    <DataCell>
                      <TransactionProcessor transaction={transaction} />
                    </DataCell>
                  </BaseTableDataRow>
                );
              })
            )}
          </BaseTableBody>
        </BaseTable>
      </div>
    </div>
  );
}

// Contribution Tab Component
function ContributionTab({ data }: { data: any }) {
  const { t: tContribution, i18n } = useTranslation('', {
    keyPrefix: 'page.financial.memberInformation.contribution',
  });

  return (
    <div className="w-full">
      {/* Stats Tiles */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TbCash className="h-8 w-8 text-primary-300" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-500">{tContribution('tiles.balance')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatEuroCentsWithSign(data.stats.balance, i18n.language)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TbCash className="h-8 w-8 text-success" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-500">{tContribution('tiles.paid')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatEuroCents(data.stats.income, i18n.language)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TbCash className="h-8 w-8 text-secondary" />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-500">{tContribution('tiles.due')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatEuroCents(Math.abs(data.stats.expenses), i18n.language)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="relative w-full overflow-x-auto">
        <BaseTable>
          <BaseTableHeader>
            <BaseTableHeaderRow>
              <HeaderCell>{tContribution('table.date')}</HeaderCell>
              <HeaderCell>{tContribution('table.description')}</HeaderCell>
              <HeaderCell>{tContribution('table.amount')}</HeaderCell>
              <HeaderCell>{tContribution('table.contributionPlan')}</HeaderCell>
            </BaseTableHeaderRow>
          </BaseTableHeader>
          <BaseTableBody>
            <BaseTableSpacerRow />
            {data.transactions.length === 0 ? (
              <BaseTableDataRow>
                <td colSpan={4} className="py-8 text-center text-gray-500">
                  {tContribution('noTransactions')}
                </td>
              </BaseTableDataRow>
            ) : (
              data.transactions.map((transaction: any) => (
                <BaseTableDataRow key={transaction.transactionId}>
                  <DataCell>{formatDate(transaction.date)}</DataCell>
                  <DataCell>{transaction.description}</DataCell>
                  <DataCell>{formatEuroCentsWithSign(transaction.amount, i18n.language)}</DataCell>
                  <DataCell>
                    {transaction.contributionPayment?.contributionPlan?.name ||
                      tContribution('table.noPlan')}
                  </DataCell>
                </BaseTableDataRow>
              ))
            )}
          </BaseTableBody>
        </BaseTable>
      </div>
    </div>
  );
}

interface SalesTabProps {
  connectionId: number;
}
// Sales Tab Component
function SalesTab({ connectionId }: SalesTabProps) {
  const { t: tSales } = useTranslation('', { keyPrefix: 'page.financial.memberInformation.sales' });

  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.orders' });

  const navigate = useNavigate();

  const connections = useSelector((state: RootState) => state.connections).filter(
    (c) => c.type === ConnectionType.B2C_CONNECTION,
  );

  const connection = connections.find((c) => +c.id === connectionId);

  const [orders, setOrders] = useState<C2BOrderDto[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<C2BOrderDto | null>(null);

  useEffect(() => {
    loadData();
  }, [connectionId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load orders
      const fetchedOrders = await getOrdersForUser(connection?.userId || -1);
      setOrders(fetchedOrders);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;

    try {
      await deleteOrder(orderToDelete.c2bOrderId);
      setOrders(orders.filter((o) => o.c2bOrderId !== orderToDelete.c2bOrderId));
      setOrderToDelete(null);
      setDeleteModalOpen(false);
      toast.success('Order deleted successfully');
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  };

  const openDeleteModal = (order: C2BOrderDto) => {
    setOrderToDelete(order);
    setDeleteModalOpen(true);
  };

  const handleBackToReports = () => {
    navigate('/financial/reports', { state: { activeTab: 'members' } });
  };

  const getStatusBadge = (status: PaymentOrderStatus) => {
    const statusConfig = {
      [PaymentOrderStatus.PENDING]: {
        label: t('pending'),
        className: 'bg-orange-100 text-secondary',
      },
      [PaymentOrderStatus.PAID]: { label: t('paid'), className: 'bg-green-100 text-success' },
      [PaymentOrderStatus.AUTHORIZED]: {
        label: t('authorized'),
        className: 'bg-green-100 text-success',
      },
      [PaymentOrderStatus.CANCELLED]: {
        label: t('cancelled'),
        className: 'bg-red-100 text-danger',
      },
      [PaymentOrderStatus.FAILED]: { label: t('failed'), className: 'bg-red-100 text-danger' },
      [PaymentOrderStatus.REFUNDED]: {
        label: t('refunded'),
        className: 'bg-purple-100 text-purple-800',
      },
      [PaymentOrderStatus.TO_BE_REFUNDED]: {
        label: t('toBeRefunded'),
        className: 'bg-purple-60 text-purple-600',
      },
    };

    const config = statusConfig[status] || {
      label: 'Unknown',
      className: 'bg-gray-100 text-gray-800',
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  // Filter orders by product if productId is provided

  // Calculate tile data for selected product
  const tileData = React.useMemo(() => {
    if (!orders) return null;

    const totalOrders = orders.length;
    const paidOrders = orders.filter((order) => order.status === PaymentOrderStatus.PAID);
    const totalCollected = paidOrders.reduce((sum, order) => sum + order.amount, 0);
    const pendingOrders = orders.filter((order) => order.status === PaymentOrderStatus.PENDING);

    return {
      totalOrders,
      pendingOrders: pendingOrders.length,
      amountCollected: totalCollected,
    };
  }, [orders]);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center">
        <Button variant="tertiary" onClick={handleBackToReports} className="mr-4">
          <FaArrowLeft className="mr-2 h-4 w-4" />
          {t('backToReports')}
        </Button>
      </div>

      <PageHeader
        title={`${connection?.fields.FIRST_NAME} ${connection?.fields.LAST_NAME}` || t('title')}
        subtitle={t('substitleUserOrder')}
      />

      {/* Stats Tiles */}
      {tileData && (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total Orders Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaUsers className="h-8 w-8 text-primary-300" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{tileData.totalOrders}</p>
              </div>
            </div>
          </div>

          {/* Pending Orders Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaCalendar className="h-8 w-8 text-secondary" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Pending Orders</p>
                <p className="text-2xl font-bold text-gray-900">{tileData.pendingOrders}</p>
              </div>
            </div>
          </div>

          {/* Amount Collected Tile */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaMoneyBill className="h-8 w-8 text-success" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Amount Collected</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatEuroCents(tileData.amountCollected, i18n.language)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="relative w-full overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-lg text-gray-500">Loading orders...</div>
          </div>
        ) : orders.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mb-4 text-lg text-gray-500">
              {connection
                ? `No payment requests for "${`${connection?.fields.FIRST_NAME} ${connection?.fields.LAST_NAME}` || ''}" yet`
                : 'No payment requests yet'}
            </div>
          </div>
        ) : (
          <BaseTable>
            <BaseTableHeader>
              <BaseTableHeaderRow>
                <HeaderCell>{t('type')}</HeaderCell>
                <HeaderCell>{t('requestDate')}</HeaderCell>
                <HeaderCell>{t('status')}</HeaderCell>
                <HeaderCell>{t('amountPaid')}</HeaderCell>
                <HeaderCell>{t('paymentDate')}</HeaderCell>
                <HeaderCell>{t('actions')}</HeaderCell>
              </BaseTableHeaderRow>
            </BaseTableHeader>
            <BaseTableBody>
              <BaseTableSpacerRow />
              {orders.map((order) => {
                return (
                  <BaseTableDataRow key={order.c2bOrderId}>
                    <DataCell>
                      <div className="text-sm font-medium text-gray-900">{t(order.type)}</div>
                    </DataCell>
                    <DataCell>{formatDate(order.createdAt)}</DataCell>
                    <DataCell>{getStatusBadge(order.status)}</DataCell>
                    <DataCell>
                      {order.status !== PaymentOrderStatus.PENDING &&
                      order.status !== PaymentOrderStatus.CANCELLED
                        ? formatEuroCents(order.amount, i18n.language)
                        : '-'}
                    </DataCell>
                    <DataCell>
                      {order.status !== PaymentOrderStatus.PENDING &&
                      order.status !== PaymentOrderStatus.CANCELLED
                        ? formatDate(order.updatedAt)
                        : '-'}
                    </DataCell>
                    <DataCell>
                      <ActionMenu>
                        {order.status === PaymentOrderStatus.PENDING && (
                          <ActionMenuButton
                            icon={FaTrash}
                            text={t('delete')}
                            className="text-danger"
                            onClick={() => openDeleteModal(order)}
                          />
                        )}
                      </ActionMenu>
                    </DataCell>
                  </BaseTableDataRow>
                );
              })}
            </BaseTableBody>
          </BaseTable>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AreYouSureModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title="Delete Payment Request"
        description={`Are you sure you want to delete this payment request for "${orderToDelete?.customerName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteOrder}
        saveClassName="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
}

interface HeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  className?: string;
}

function HeaderCell({ className, ...props }: HeaderCellProps): JSX.Element {
  return <th className={`py-3 pl-1 text-primary-900 ${className || ''}`} {...props} />;
}

interface DataCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  className?: string;
}

function DataCell({ className, ...props }: DataCellProps): JSX.Element {
  return (
    <td
      className={`whitespace-nowrap py-4 pl-2 text-sm font-medium text-primary-900 ${className || ''}`}
      {...props}
    />
  );
}
